import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import '@adornis/file-utils/client/x-file-input.js';
import '@adornis/file-utils/client/x-image.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import '@adornis/internationalization/client/x-tel-input.js';
import '@adornis/popover/x-dropdown-selection.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { Language as Languages } from '@adornis/translation-core/languages.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { registerUser } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { passwordRegexCheck } from '@adornis/users/db/validators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import '@ils/permissions/client/ils-role-picker.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import {
  FederalStatesGermany,
  Gender,
  OSCECategory,
  OsceCategories,
  Specialization,
  UserRole,
  isTextOsceCategory,
  prettyOsceCategory,
} from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { ILSUser } from '../../db/ils-user.js';

// TODO maybe save some osce metadata on the categories like examType, videoEnabled, taskType
const osceSelection = OsceCategories.filter(type => !isTextOsceCategory(type));

const passwordErrorMsg =
  'Das Passwort muss mindestens 8 Zeichen lang sein,\neinen Großbuchstaben, eine Zahl und ein Sonderzeichen beinhalten.';

const userSelector = {
  _id: 1,
  roles: { name: 1, contexts: 1 },
  email: 1,
  username: 1,
  ecommerce: {
    firstName: 1,
    lastName: 1,
    fullName: 1,
    street: 1,
    zip: 1,
    city: 1,
    country: 1,
    phone: 1,
    acceptedLegal: 1,
  },
  profile: {
    gender: 1,
    birthplace: 1,
    birthCountry: 1,
    targetFederalState: 1,
    b2Certificate: 1,
    dateOfBirth: 1,
    hasParticipatedExam: 1,
    participatedC1Course: 1,
    wantsNewsletter: 1,
  },
  coInvoice: 1,
  specialization: 1,
  assignedOSCETypes: 1,
} as const satisfies BaseQLSelectionSet<ILSUser>;

type NarrowedUser = NarrowedEntity<ILSUser, typeof userSelector>;

/**
 * @element ils-user-edit
 */
@customElement('ils-user-edit')
export class ILSUserEdit extends XDialog<void> {
  static override get element_name() {
    return 'ils-user-edit';
  }
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': { maxWidth: '70vw', width: '70vw' },
      },
    ];
  }

  @property({ attribute: false }) userID = new RXController(this, undefined as Maybe<string>);

  @property({ attribute: false }) createUser = new RXController(this, undefined as Maybe<NarrowedUser>);

  @state() user = new RXController(
    this,
    this.userID.observable.pipe(
      switchMap(userID => {
        if (!userID) return of(null);
        return getByID(ILSUser, userID)(userSelector);
      }),
    ),
  );

  @state() password = '';
  @state() passwordError = '';

  @state() userRole?: UserRole;

  @state() userSaving = false;

  @state() private readonly _form = new FormController(
    this,
    combineLatest([this.user.observable, this.createUser.observable]).pipe(
      map(([user, createUser]) => user ?? createUser),
    ),
    {
      onSuccess: user => {
        if (!user.roles.length) {
          throw new ValidationError('Nutzer muss mindestens eine Rolle haben.', {
            key: 'roles',
          });
        }
      },
      success: async user => {
        if (!this.user.value && !passwordRegexCheck(this.password)) {
          this.passwordError = passwordErrorMsg;
          logger.error('Das Passwort ist nicht gütlig.');
          return;
        }

        this.userSaving = true;

        if (this.user.value) {
          await this.user.value.save();
        } else {
          await registerUser({
            // @ts-expect-error Permission typing is broken, but ILSUser extends AdornisUser so this works
            userClass: ILSUser,
            username: A.getGloballyUniqueID(),
            email: user.email,
            password: this.password,
            additionalFields: {
              ecommerce: user.ecommerce,
              profile: user.profile,
              roles: user.roles,
              specialization: user.specialization,
              coInvoice: user.coInvoice,
              assignedOSCETypes: user.assignedOSCETypes,
            },
          });
        }
        this.userSaving = false;
        void XSnackbar.show(`Nutzer ${user.ecommerce?.fullName} gespeichert!`);
        void this.close();
      },
      error: error => {
        this.userSaving = false;
        logger.error(error);
        void XDialog.alert(error.message);
      },
    },
  );

  protected override willUpdate() {
    if (!this.createUser.value && !this.userID.value) {
      this.createUser.next(
        new ILSUser({
          username: 'NO_NEED_FOR_USERNAME',
          profile: { b2Certificate: '_file_' },
          ecommerce: { acceptedLegal: true },
          roles: this.userRole
            ? [
                new AdornisRoleToContextsWrapper({
                  name: this.userRole,
                  contexts: [UserRole.INSPECTOR, UserRole.EXAMCENTER].includes(this.userRole) ? [] : [GLOBAL_CONTEXT],
                }),
              ]
            : [],
          assignedOSCETypes: [],
        }) as any as NarrowedUser,
      );
    }
  }

  override render() {
    return html`
      <x-flex padding="md" space="sm">
        <x-h2> Nutzer ${this.userID.value ? 'editieren' : 'anlegen'} </x-h2>
        ${permissionGuard({
          resource: 'users',
          action: this.userID.value ? 'modify' : 'create',
          data: this.user.value,
          guardedTemplate: this.userView(),
          forbiddenTemplate: html`
            <x-text> Keine Berechtigung Nutzer ${this.userID.value ? 'zu editieren' : 'anzulegen'} </x-text>
          `,
        })}
        ${this._buttons()}
      </x-flex>
    `;
  }

  userView() {
    if (this.userID.value && this.user.isLoading)
      return html` <x-flex crossaxis-center center w100> <ils-loader></ils-loader> </x-flex> `;
    return html`
      ${this._generate2FAButton()}
      <ils-role-picker ${this._form.field('roles')} @value-picked=${() => this.requestUpdate()}></ils-role-picker>

      ${this._inspectorSpecializationSelection()} ${this._testerOsceSelection()} ${this._basicInfoInputs()}
      ${this._passwordInput()} ${this._genderInput()} ${this._contactInputs()} ${this._additionalTraineeInfo()}
    `;
  }
  private _inspectorSpecializationSelection(): Renderable {
    if (!this._form.document?.roles.find(role => role.name === UserRole.INSPECTOR)) return nothing;
    return html`
      <x-dropdown-selection
        ${this._form.field('specialization')}
        placeholder="Spezialisierung"
        .selectables=${Object.values(Specialization)}
      ></x-dropdown-selection>
    `;
  }

  private _generate2FAButton(): Renderable {
    if (!this.user.value?._id) return nothing;
    return html`
      <x-flex horizontal end>
        <x-button
          mode="outline"
          @click=${async () => {
            if (!this.user.value) return;
            try {
              const qr = await XDialog.waitFor(ILSUser.generate2FAQRCode(this.user.value._id)());
              if (qr)
                await XDialog.showPopup({
                  content: resolve => html`
                    <x-flex padding="md">
                      <x-image disabled .value=${qr} is-url mode="width" ${css({ width: '250px' })}> </x-image>
                    </x-flex>
                  `,
                }).catch(() => {
                  /* ignore */
                });
              else void XDialog.alert('2 FA Code existiert bereits');
            } catch (e) {
              logger.error({ err: e }, 'Es gab ein Problem beim generieren des 2FA Codes');
              void XDialog.alert('Es gab ein Problem beim Generieren des 2FA-Codes. Bitte versuche es später erneut');
            }
          }}
        >
          2FA Generieren
        </x-button>
      </x-flex>
    `;
  }

  private _testerOsceSelection(): Renderable {
    if (!this._form.document) return nothing;
    if (!this._form.document.roles.find(role => role.name === UserRole.TESTER)) return nothing;
    const user = this._form.document;
    return html`
      <x-dropdown-multiselection
        .renderItem=${(osce: OSCECategory) => html`
          <x-flex horizontal crossaxis-center space="sm" ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}>
            <x-checkbox disabled .value=${user.assignedOSCETypes?.includes(osce) ?? false}></x-checkbox>
            <x-text> ${prettyOsceCategory(osce)} </x-text>
          </x-flex>
        `}
        .renderSelected=${(selected?: OSCECategory[]) => {
          if (!selected?.length) return '';
          if (!selected[0]) return '';
          if (selected.length === 1) return prettyOsceCategory(selected[0]);
          return `${prettyOsceCategory(selected[0])} + ${selected.length - 1}`;
        }}
        .selectables=${osceSelection}
        placeholder="OSCEs zuweisen"
        placeholder-mode="hide"
        ${this._form.field('assignedOSCETypes')}
      ></x-dropdown-multiselection>
    `;
  }

  private _genderInput(): Renderable {
    if (!this._form.document?.roles.find(role => role.name === UserRole.TRAINEE)) return nothing;
    return html`
      <x-dropdown-selection
        ${this._form.field('profile', 'gender')}
        placeholder="Geschlecht"
        .selectables=${Object.values(Gender)}
      ></x-dropdown-selection>
    `;
  }

  private _basicInfoInputs(): Renderable {
    return html`
      <x-input ${this._form.field('ecommerce', 'firstName')} placeholder="Vorname"></x-input>
      <x-input ${this._form.field('ecommerce', 'lastName')} placeholder="Nachname"></x-input>
      <x-input ${this._form.field('email')} placeholder="E-Mail"></x-input>
    `;
  }
  private _contactInputs(): Renderable {
    return html`
      <x-input ${this._form.field('ecommerce', 'street')} placeholder="Adresse"></x-input>
      <x-flex horizontal space="md" crossaxis-end>
        <x-input ${this._form.field('ecommerce', 'zip')} placeholder="PLZ" ${css({ maxWidth: '30%' })}></x-input>
        <x-input flex ${this._form.field('ecommerce', 'city')} placeholder="Stadt"></x-input>
      </x-flex>
      <x-country-picker
        placeholder="Land"
        ${this._form.field('ecommerce', 'country')}
        .language=${Languages.GERMAN}
        empty-state="Geben Sie den Namen Ihres Landes ein"
      ></x-country-picker>
      <x-tel-input searchable ${this._form.field('ecommerce', 'phone')} placeholder="Telefonnummer"></x-tel-input>
    `;
  }

  private _additionalTraineeInfo(): Renderable {
    if (!this._form.document?.roles.find(role => role.name === UserRole.TRAINEE)) return nothing;
    return html`
      <x-input type="date" ${this._form.field('profile', 'dateOfBirth')} placeholder="Geburtsdatum"></x-input>
      <x-input ${this._form.field('profile', 'birthplace')} placeholder="Geburtsort"></x-input>
      <x-country-picker
        ${this._form.field('profile', 'birthCountry')}
        placeholder="Geburtsland"
        .language=${Languages.GERMAN}
        empty-state="Geben Sie den Namen Ihres Geburtslandes ein"
      ></x-country-picker>
      <x-input ${this._form.field('coInvoice')} placeholder="c/o (Rechnung)"></x-input>
      <x-dropdown-selection
        placeholder="In welchem Bundesland wollen Sie ihren Approbationsantrag stellen?*"
        .selectables=${Object.values(FederalStatesGermany)}
        ${this._form.field('profile', 'targetFederalState')}
        @value-picked=${() => this.requestUpdate()}
      >
      </x-dropdown-selection>
      ${this._form.document.profile?.targetFederalState !== FederalStatesGermany.BAYERN
        ? html`
            <x-flex horizontal space="md">
              <x-file-input
                flex
                ${this._form.field('profile', 'b2Certificate')}
                placeholder="B2 Zertifikat"
              ></x-file-input>
              <x-button
                icon="download"
                @click=${() => {
                  if (this._form.document?.profile?.b2Certificate) {
                    const file = AdornisFile.getServeLink(this._form.document.profile.b2Certificate);
                    goTo(file, { target: '_blank' });
                  }
                }}
              >
                Herunterladen
              </x-button>
            </x-flex>
          `
        : nothing}
      <x-toggle-button
        reverse
        ${css({ alignSelf: 'flex-start', marginBottom: this.spacing.sm })}
        ${this._form.field('profile', 'wantsNewsletter')}
        label="Newsletter abonniert"
      ></x-toggle-button>
    `;
  }

  private _passwordInput(): Renderable {
    if (this.user.value) return nothing;
    return html`
      <x-input
        type="password"
        placeholder="Passwort"
        @value-changed="${(e: CustomEvent) => {
          if (e.detail.value && this.password !== e.detail.value) {
            this.password = e.detail.value;
            if (!passwordRegexCheck(this.password)) this.passwordError = passwordErrorMsg;
            else this.passwordError = '';
          }
        }}"
        .error=${!this.passwordError ? undefined : this.passwordError}
      ></x-input>
    `;
  }

  private _buttons() {
    return html`
      <x-flex horizontal end space="md">
        <x-button .disabled=${this.userSaving} mode="outline" @click=${() => this.cancel()}> Zurück </x-button>
        ${permissionGuard({
          resource: 'users',
          action: this.userID.value ? 'modify' : 'create',
          data: this.user.value,
          guardedTemplate: html`
            <x-button .loading=${this.userSaving} @click=${() => this._form.validate({ validateOmitted: false })}>
              ${this.userID.value ? 'Speichern' : 'Anlegen'}
            </x-button>
          `,
        })}
      </x-flex>
    `;
  }

  override cancel() {
    this._form.document?.revert();
    return super.cancel();
  }

  override getInnerWrapper(options: Required<DialogOptions>): HTMLDivElement {
    const innerWrapper = super.getInnerWrapper(options);
    innerWrapper.style.width = '70vmax';
    innerWrapper.style.maxWidth = '70vmax';
    return innerWrapper;
  }
}
